// Intake wizard (parity slice 3b) — the animal sub-wizard's photo/AI calls the generic Refine
// dataProvider can't make (multipart bodies, bearer-only token). Twin of src/api/animalPhoto.ts but
// under the intake authority: the wizard has no animal id pre-create, so the AI analysis endpoints
// are draft-agnostic, and the captured photo is uploaded only after /complete returned the created
// animal ids (POST /api/intake/animals/{id}/photo — best-effort, non-blocking).
import { getAccessToken } from "../auth";
import { AnimalPhotoApiError, type PhotoAiAnalyzeResult } from "./animalPhoto";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function postMultipart(url: string, file: File | File[]): Promise<PhotoAiAnalyzeResult> {
  const form = new FormData();
  // Meerdere keren dezelfde veldnaam: zo leest de server het als een lijst, en blijft één bestand
  // precies hetzelfde verzoek als voorheen.
  for (const f of Array.isArray(file) ? file : [file]) {
    form.append("file", f);
  }
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new AnimalPhotoApiError(res.status, res.statusText);
  return res.json() as Promise<PhotoAiAnalyzeResult>;
}

/** Analyse an animal photo (no animal id yet) and return field suggestions. Throws on HTTP failure. */
export function analyzeIntakePhoto(file: File): Promise<PhotoAiAnalyzeResult> {
  return postMultipart(`${API_URL}/intake/reference/photo/ai-analyze`, file);
}

/**
 * "Scan foto van paspoort": identity prefill off a passport photo. Throws on HTTP failure.
 *
 * <p>Meerdere bladzijden mogen: een nieuw paspoortboekje heeft er twee met gegevens, en dan staat de
 * chip op de ene en de geboortedatum op de andere. Ze gaan samen naar het model, als één document.
 */
export function analyzeIntakePassport(file: File | File[]): Promise<PhotoAiAnalyzeResult> {
  return postMultipart(`${API_URL}/intake/reference/passport/ai-analyze`, file);
}

/** Post-complete: store the wizard's captured photo as the created animal's primary photo. */
export async function uploadIntakeAnimalPhoto(animalId: number, file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_URL}/intake/animals/${animalId}/photo`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new AnimalPhotoApiError(res.status, res.statusText);
}
